export const READ_ONLY_MESSAGE = 'Read-only support mode: tenant changes are disabled.';

/**
 * Server-backed support view-session RPCs. These are the only writes permitted
 * while a view session is active: they record the authoritative session state
 * and its audit event server-side (the browser can no longer assert an
 * impersonation event on its own). `end_tenant_view_session` in particular must
 * be reachable while the boundary is active so a session can be closed.
 */
export const SUPPORT_SESSION_RPCS = new Set([
  'start_tenant_view_session',
  'end_tenant_view_session',
  'expire_tenant_view_session',
]);

export interface BoundaryState {
  impersonating: boolean;
}

let state: BoundaryState = { impersonating: false };

export function setPlatformTenantImpersonation(active: boolean): void {
  state = { impersonating: active };
}

export function getPlatformTenantImpersonation(): boolean {
  return state.impersonating;
}

const READ_RPCS = new Set([
  'get_org_booking_availability', 'get_org_busy_intervals', 'get_org_by_domain', 'get_org_by_slug',
  'get_platform_overview', 'get_platform_settings', 'get_support_snapshot', 'get_team_member_role',
  'get_tenant_detail', 'has_role', 'is_org_suspended', 'list_audit_log', 'list_tenants',
]);
const READ_FUNCTIONS = new Set(['google-places']);
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isReadOnlyFunctionRequest(name: string, body?: BodyInit | null): boolean {
  if (READ_FUNCTIONS.has(name)) return true;
  if (name !== 'admin-theme-settings' || typeof body !== 'string') return false;

  try {
    const payload = JSON.parse(body) as { action?: unknown };
    return payload.action === 'fetch';
  } catch {
    return false;
  }
}

export interface RequestClassification {
  blocked: boolean;
  kind: 'read' | 'rest-write' | 'rpc-write' | 'storage-write' | 'function-write' | 'graphql-write' | 'other';
}

/** Pure request classifier used by the Supabase fetch boundary and tests. */
export function classifyTenantRequest(
  input: RequestInfo | URL,
  method = 'GET',
  boundaryState: BoundaryState = state,
  body?: BodyInit | null,
): RequestClassification {
  if (!boundaryState.impersonating) return { blocked: false, kind: 'other' };

  const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
  const verb = method.toUpperCase();
  const path = url.pathname;

  if (path.includes('/rest/v1/rpc/')) {
    const rpc = decodeURIComponent(path.split('/rest/v1/rpc/')[1]?.split('/')[0] ?? '');
    const allowed = SUPPORT_SESSION_RPCS.has(rpc) || READ_RPCS.has(rpc);
    return { blocked: !allowed, kind: allowed ? 'read' : 'rpc-write' };
  }

  if (path.includes('/rest/v1/')) {
    const blocked = WRITE_METHODS.has(verb);
    return { blocked, kind: blocked ? 'rest-write' : 'read' };
  }

  if (path.includes('/storage/v1/object/')) {
    const blocked = WRITE_METHODS.has(verb);
    return { blocked, kind: blocked ? 'storage-write' : 'read' };
  }

  if (path.includes('/functions/v1/')) {
    const name = decodeURIComponent(path.split('/functions/v1/')[1]?.split('/')[0] ?? '');
    const allowed = isReadOnlyFunctionRequest(name, body);
    return { blocked: !allowed, kind: allowed ? 'read' : 'function-write' };
  }

  if (path.includes('/graphql/v1')) {
    const blocked = WRITE_METHODS.has(verb);
    return { blocked, kind: blocked ? 'graphql-write' : 'read' };
  }

  return { blocked: false, kind: 'other' };
}

/** Fetch implementation supplied to Supabase. Throws before network dispatch. */
export function platformReadOnlyFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const method = init?.method ?? (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET');
  const classification = classifyTenantRequest(input, method, state, init?.body);
  if (classification.blocked) {
    return Promise.reject(new Error(READ_ONLY_MESSAGE));
  }
  return fetch(input, init);
}
